import { useState } from 'react';
import { logger, LogLevel } from '@/utils/logger';
import { Bug, Trash2, ChevronDown, Info, AlertTriangle, XCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Componente de controle do sistema de logging
 * Aparece apenas em modo de desenvolvimento
 */
export function LoggerControl() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(() => logger.getConfig().level);
  const [isEnabled, setIsEnabled] = useState(() => logger.getConfig().enabled);

  // Não renderiza em produção
  if (import.meta.env.PROD) {
    return null;
  }

  const handleClearLogs = () => {
    logger.clear();
  };

  const handleToggleEnabled = () => {
    const newState = !isEnabled;
    logger.setEnabled(newState);
    setIsEnabled(newState);
  };

  const handleSetLevel = (level: LogLevel) => {
    logger.setLevel(level);
    setCurrentLevel(level);
  };

  const getLevelColor = (level: LogLevel): string => {
    switch (level) {
      case LogLevel.DEBUG: return 'text-muted-foreground';
      case LogLevel.INFO: return 'text-primary';
      case LogLevel.WARN: return 'text-foreground';
      case LogLevel.ERROR: return 'text-destructive';
      case LogLevel.NONE: return 'text-muted-foreground/70';
      default: return 'text-muted-foreground';
    }
  };

  const getLevelBgColor = (level: LogLevel, isActive: boolean): string => {
    if (!isActive) return 'bg-muted hover:bg-accent';

    switch (level) {
      case LogLevel.INFO: return 'bg-primary/10 hover:bg-primary/15';
      case LogLevel.ERROR: return 'bg-destructive/10 hover:bg-destructive/15';
      default: return 'bg-secondary hover:bg-accent';
    }
  };

  const getLevelIcon = (level: LogLevel) => {
    const iconClass = `h-4 w-4 ${getLevelColor(level)}`;
    switch (level) {
      case LogLevel.DEBUG: return <Bug className={iconClass} />;
      case LogLevel.INFO: return <Info className={iconClass} />;
      case LogLevel.WARN: return <AlertTriangle className={iconClass} />;
      case LogLevel.ERROR: return <XCircle className={iconClass} />;
      case LogLevel.NONE: return <XCircle className="h-4 w-4 text-muted-foreground/70" />;
      default: return null;
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Painel de controles */}
      {isOpen && (
        <div className="mb-2 bg-card rounded-lg shadow-2xl border border-border p-4 w-64 animate-in slide-in-from-bottom-2">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-primary" aria-hidden="true" />
              <h3 className="font-semibold text-sm text-foreground">Controle de logs</h3>
            </div>
          </div>

          {/* Toggle Enable/Disable */}
          <div className="mb-3 pb-3 border-b border-border">
            <button
              onClick={handleToggleEnabled}
              className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isEnabled
                  ? 'bg-primary/10 text-primary hover:bg-primary/15'
                  : 'bg-muted text-foreground hover:bg-accent'
              }`}
            >
              {isEnabled ? '✓ Logs habilitados' : '✗ Logs desabilitados'}
            </button>
          </div>

          {/* Níveis de log */}
          {isEnabled && (
            <>
              <div className="mb-2">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Nível de Log
                </label>
                <div className="space-y-1">
                  {[
                    { level: LogLevel.DEBUG, label: 'DEBUG', desc: 'Tudo' },
                    { level: LogLevel.INFO, label: 'INFO', desc: 'Informações' },
                    { level: LogLevel.WARN, label: 'WARN', desc: 'Avisos' },
                    { level: LogLevel.ERROR, label: 'ERROR', desc: 'Erros' },
                    { level: LogLevel.NONE, label: 'NONE', desc: 'Nenhum' },
                  ].map(({ level, label, desc }) => (
                    <button
                      key={level}
                      onClick={() => handleSetLevel(level)}
                      className={`w-full px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-between ${getLevelBgColor(
                        level,
                        currentLevel === level
                      )}`}
                    >
                      <div className="flex items-center gap-2">
                        {getLevelIcon(level)}
                        <span className={getLevelColor(level)}>{label}</span>
                      </div>
                      <span className="text-muted-foreground text-xs">{desc}</span>
                      {currentLevel === level && (
                        <span className="ml-2">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Botão de limpar */}
              <div className="mt-3 pt-3 border-t border-border">
                <button
                  onClick={handleClearLogs}
                  className="w-full px-3 py-2 bg-secondary text-secondary-foreground hover:bg-accent rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Limpar Console
                </button>
              </div>
            </>
          )}

          {/* Info */}
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Sessão: {logger.getConfig().sessionId.substring(0, 12)}...
            </p>
          </div>
        </div>
      )}

      {/* Botao flutuante */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-3 shadow-lg transition-all transform hover:scale-110 ${
              isOpen ? 'rotate-180' : ''
            }`}
            aria-label={isOpen ? 'Fechar controle de logs' : 'Abrir controle de logs'}
          >
            {isOpen ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <Bug className="h-5 w-5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>{isOpen ? 'Fechar controle de logs' : 'Abrir controle de logs'}</TooltipContent>
      </Tooltip>

      {/* Badge de status */}
      {!isOpen && (
        <div className="absolute -top-1 -left-1 h-3 w-3">
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${
              isEnabled ? 'bg-primary/60' : 'bg-muted-foreground/40'
            }`}
          />
          <span
            className={`relative inline-flex rounded-full h-3 w-3 ${
              isEnabled ? 'bg-primary' : 'bg-muted-foreground'
            }`}
          />
        </div>
      )}
    </div>
  );
}

export default LoggerControl;
